import { Navigate, Outlet, RouteObject } from 'react-router-dom';

import { lazyWithRetry } from '@/shared/lib/lazyWithRetry';

import { TAdminDictionaryResource } from '@/shared/types/admin-api';

import {
  ADMIN_ROUTE_META,
  ADMIN_ROUTES,
  IAdminRouteMeta
} from '@/shared/constants';

import { ADMIN_GENERIC_RESOURCE_CONFIG } from '@/entities/admin-resources';

import { AdminProtectedRoute } from '@/features/admin-auth';

import { AdminLayout } from '@/widgets/admin-layout';

import { AdminComposition } from '../di/compositions/Admin';
import { LoggedOutUserComposition } from '../di/compositions/LoggedOutUser';
import { sentryCreateBrowserRouter } from '../sentry';

const AdminCastingFormPage = lazyWithRetry(
  () => import('@/pages/admin/casting-form')
);
const AdminGenericResourcePage = lazyWithRetry(
  () => import('@/pages/admin/generic')
);
const AdminLoginPage = lazyWithRetry(() => import('@/pages/admin/login'));
const AdminPasswordResetPage = lazyWithRetry(
  () => import('@/pages/admin/password-reset')
);
const AdminResourceListPage = lazyWithRetry(
  () => import('@/pages/admin/resource-list')
);

const readyResources: Array<{
  path: string;
  resource: TAdminDictionaryResource;
}> = [
  { path: 'skills', resource: 'skills' },
  { path: 'languages', resource: 'languages' },
  { path: 'interests', resource: 'interests' }
];

/**
 * Превращает absolute `/admin-v2/...` в child path внутри `/admin-v2`
 * @param to Абсолютный маршрут
 */
const toAdminChildPath = (to: string) => {
  return to.replace(`${ADMIN_ROUTES.root}/`, '');
};

/**
 * Создаёт route для generic admin resource.
 * @param meta Метаданные route
 */
const createGenericResourceRoute = (meta: IAdminRouteMeta): RouteObject => {
  const config = ADMIN_GENERIC_RESOURCE_CONFIG[meta.to];

  if (!config) {
    throw new Error(`Admin route is not mapped to API: ${meta.to}`);
  }

  return {
    path: toAdminChildPath(meta.to),
    element: (
      <AdminGenericResourcePage key={`list:${meta.to}`} config={config} />
    )
  };
};

/**
 * Создаёт edit route для generic admin resource.
 * @param meta Метаданные list route
 */
const createGenericResourceEditRoute = (meta: IAdminRouteMeta): RouteObject => {
  const config = ADMIN_GENERIC_RESOURCE_CONFIG[meta.to];

  if (!config) {
    throw new Error(`Admin route is not mapped to API: ${meta.to}`);
  }

  return {
    path: `${toAdminChildPath(meta.to)}/:id/edit`,
    element: (
      <AdminGenericResourcePage
        key={`edit:${meta.to}`}
        config={{ ...config, mode: 'form' }}
      />
    )
  };
};

/**
 * Создаёт create route для generic admin resource.
 * @param meta Метаданные list route
 */
const createGenericResourceCreateRoute = (
  meta: IAdminRouteMeta
): RouteObject => {
  const config = ADMIN_GENERIC_RESOURCE_CONFIG[meta.to];

  if (!config) {
    throw new Error(`Admin route is not mapped to API: ${meta.to}`);
  }

  return {
    path: `${toAdminChildPath(meta.to)}/new`,
    element: (
      <AdminGenericResourcePage
        key={`new:${meta.to}`}
        config={{ ...config, mode: 'form' }}
      />
    )
  };
};

/**
 * Создаёт detail route для generic admin resource.
 * @param meta Метаданные list route
 */
const createGenericResourceDetailRoute = (
  meta: IAdminRouteMeta
): RouteObject => {
  const config = ADMIN_GENERIC_RESOURCE_CONFIG[meta.to];

  if (!config) {
    throw new Error(`Admin route is not mapped to API: ${meta.to}`);
  }

  return {
    path: `${toAdminChildPath(meta.to)}/:id`,
    element: (
      <AdminGenericResourcePage
        key={`detail:${meta.to}`}
        config={{ ...config, apiPath: `${config.apiPath}/:id`, mode: 'detail' }}
      />
    )
  };
};

const formRoutes = [ADMIN_ROUTES.castings.new, ADMIN_ROUTES.castings.edit()];
const explicitDetailListRoutes: string[] = [
  ADMIN_ROUTES.users.index,
  ADMIN_ROUTES.projects.index,
  ADMIN_ROUTES.castings.index
];

const genericResourceRoutes = ADMIN_ROUTE_META.filter(
  meta =>
    !readyResources.some(
      resource => meta.to === `${ADMIN_ROUTES.root}/${resource.path}`
    ) &&
    !formRoutes.includes(meta.to) &&
    !meta.titleOnly
).map(createGenericResourceRoute);

const genericResourceEditRoutes = ADMIN_ROUTE_META.filter(meta => {
  const config = ADMIN_GENERIC_RESOURCE_CONFIG[meta.to];

  return (
    Boolean(config?.editFields?.length) &&
    config?.mode === 'list' &&
    meta.to !== ADMIN_ROUTES.castings.index
  );
}).map(createGenericResourceEditRoute);

const genericResourceCreateRoutes = ADMIN_ROUTE_META.filter(meta => {
  const config = ADMIN_GENERIC_RESOURCE_CONFIG[meta.to];

  return (
    Boolean(config?.canCreate) &&
    config?.mode === 'list' &&
    meta.to !== ADMIN_ROUTES.castings.index
  );
}).map(createGenericResourceCreateRoute);

const genericResourceDetailRoutes = ADMIN_ROUTE_META.filter(meta => {
  const config = ADMIN_GENERIC_RESOURCE_CONFIG[meta.to];

  return (
    config?.mode === 'list' &&
    !readyResources.some(
      resource => meta.to === `${ADMIN_ROUTES.root}/${resource.path}`
    ) &&
    !formRoutes.includes(meta.to) &&
    !explicitDetailListRoutes.includes(meta.to)
  );
}).map(createGenericResourceDetailRoute);

// Pathless-корень обязателен: все 4 роутера приложения слушают popstate
// одновременно, поэтому Back/Forward на любой не-админской странице приходит и
// сюда. Без корневого маршрута без path (или index / '/') matchRoutes даёт null,
// а getShortCircuitMatches возвращает синтетический __shim-error-route__, которого
// нет в manifest → TypeError: reading 'element' в updateState (ACTOR-FRONT-7G).
// У actor/director/auth-роутеров такой корень есть — отсюда падал только admin
export const ADMIN_ROUTE_OBJECTS: RouteObject[] = [
  {
    element: <Outlet />,
    children: [
      {
        path: ADMIN_ROUTES.login,
        element: (
          <LoggedOutUserComposition>
            <AdminLoginPage />
          </LoggedOutUserComposition>
        )
      },
      {
        path: ADMIN_ROUTES.password_reset,
        element: (
          <LoggedOutUserComposition>
            <AdminPasswordResetPage />
          </LoggedOutUserComposition>
        )
      },
      {
        path: ADMIN_ROUTES.root,
        element: (
          <AdminComposition>
            <AdminProtectedRoute>
              <AdminLayout />
            </AdminProtectedRoute>
          </AdminComposition>
        ),
        children: [
          {
            index: true,
            element: <Navigate to={ADMIN_ROUTES.users.index} replace />
          },
          ...readyResources.map(({ path, resource }) => ({
            path,
            element: (
              <AdminResourceListPage key={resource} resource={resource} />
            )
          })),
          {
            path: toAdminChildPath(ADMIN_ROUTES.castings.new),
            element: <AdminCastingFormPage />
          },
          {
            path: toAdminChildPath(ADMIN_ROUTES.castings.edit()),
            element: <AdminCastingFormPage />
          },
          ...genericResourceCreateRoutes,
          ...genericResourceEditRoutes,
          ...genericResourceDetailRoutes,
          ...genericResourceRoutes,
          {
            path: '*',
            element: <Navigate to={ADMIN_ROUTES.users.index} replace />
          }
        ]
      }
    ]
  }
];

export const adminRouter = sentryCreateBrowserRouter(ADMIN_ROUTE_OBJECTS);
